import { useEffect, useMemo, useState } from 'react'
import { AdminLayout, useAdminAccess } from '../../components/AdminLayout'
import { useLanguage } from '../../contexts/LanguageContext'
import { requestApi } from '../../lib/adminApi'

type CreditsUser = {
  id: string
  username?: string | null
  phone?: string | null
  email?: string | null
  credits: number
  trial_credits_remaining: number
  plan: string
  created_at?: string | null
}

type CreditsResponse = {
  users: CreditsUser[]
  total: number
  limit: number
  offset: number
}

const AdminBillingContent = () => {
  const { t } = useLanguage()
  const { isGranted } = useAdminAccess()
  const [users, setUsers] = useState<CreditsUser[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const limit = 20

  const [actionType, setActionType] = useState<'adjust' | 'recharge'>('adjust')
  const [actionUserId, setActionUserId] = useState('')
  const [creditsValue, setCreditsValue] = useState('0')
  const [amountValue, setAmountValue] = useState('0')
  const [bonusValue, setBonusValue] = useState('0')
  const [reason, setReason] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const totalPages = useMemo(() => Math.ceil(total / limit), [total, limit])
  const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit])

  const fetchUsers = () => {
    setLoading(true)
    setError('')
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset)
    })
    if (search.trim()) {
      params.set('search', search.trim())
    }
    requestApi<CreditsResponse>(`/api/v1/admin/credits/users?${params.toString()}`)
      .then((payload) => {
        setUsers(payload.users || [])
        setTotal(payload.total || 0)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('admin.billing.error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!isGranted) return
    fetchUsers()
  }, [offset, isGranted])

  const handleSearch = () => {
    setOffset(0)
    fetchUsers()
  }

  const handleSelectUser = (user: CreditsUser) => {
    setActionUserId(user.id)
    setActionMessage('')
  }

  const handleSubmit = async () => {
    if (!actionUserId) {
      setActionMessage(t('admin.billing.validationUser'))
      return
    }
    setSubmitting(true)
    setActionMessage('')
    try {
      if (actionType === 'adjust') {
        await requestApi('/api/v1/admin/credits/adjust', {
          method: 'POST',
          body: JSON.stringify({
            user_id: actionUserId,
            credits: Number(creditsValue),
            reason: reason || t('admin.billing.defaultReason')
          })
        })
      } else {
        await requestApi('/api/v1/admin/credits/recharge', {
          method: 'POST',
          body: JSON.stringify({
            user_id: actionUserId,
            amount: Number(amountValue),
            bonus: Number(bonusValue),
            reason: reason || t('admin.billing.rechargeReason')
          })
        })
      }
      setActionMessage(t('admin.billing.actionSuccess'))
      fetchUsers()
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : t('admin.billing.error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
        <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('admin.billing.actionTitle')}</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.billing.fields.userId')}</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={actionUserId}
                onChange={(e) => setActionUserId(e.target.value)}
                placeholder={t('admin.billing.placeholders.userId')}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.billing.fields.actionType')}</label>
              <select
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={actionType}
                onChange={(e) => setActionType(e.target.value as 'adjust' | 'recharge')}
              >
                <option value="adjust">{t('admin.billing.actions.adjust')}</option>
                <option value="recharge">{t('admin.billing.actions.recharge')}</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.billing.fields.reason')}</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('admin.billing.placeholders.reason')}
              />
            </div>
            {actionType === 'adjust' ? (
              <div className="space-y-2">
                <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.billing.fields.credits')}</label>
                <input
                  type="number"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  value={creditsValue}
                  onChange={(e) => setCreditsValue(e.target.value)}
                />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.billing.fields.amount')}</label>
                  <input
                    type="number"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    value={amountValue}
                    onChange={(e) => setAmountValue(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">{t('admin.billing.fields.bonus')}</label>
                  <input
                    type="number"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    value={bonusValue}
                    onChange={(e) => setBonusValue(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
          {actionMessage && <p className="mt-3 text-xs text-rose-500">{actionMessage}</p>}
          <div className="mt-4">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? t('admin.billing.submitting') : t('admin.billing.submit')}
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-700">{t('admin.billing.listTitle')}</h3>
            <div className="flex gap-2">
              <input
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('admin.billing.placeholders.search')}
              />
              <button
                onClick={handleSearch}
                className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
              >
                {t('admin.billing.search')}
              </button>
            </div>
          </div>
          {loading && <p className="text-sm text-slate-400">{t('admin.billing.loading')}</p>}
          {error && <p className="text-sm text-rose-500">{error}</p>}
          {!loading && !error && (
            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('admin.billing.table.user')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.billing.table.plan')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.billing.table.credits')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.billing.table.trial')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.billing.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{user.username || user.phone || user.email || user.id}</div>
                        <div className="text-xs text-slate-400">{user.id}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{user.plan}</td>
                      <td className="px-4 py-3 text-slate-600">{user.credits}</td>
                      <td className="px-4 py-3 text-slate-600">{user.trial_credits_remaining}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleSelectUser(user)}
                          className="text-xs font-semibold text-primary hover:text-blue-700"
                        >
                          {t('admin.billing.actions.select')}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-sm text-slate-400" colSpan={5}>
                        {t('admin.billing.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{t('admin.billing.pagination').replace('{current}', String(currentPage)).replace('{total}', String(totalPages))}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
                >
                  {t('admin.billing.prev')}
                </button>
                <button
                  onClick={() => setOffset(Math.min((totalPages - 1) * limit, offset + limit))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
                >
                  {t('admin.billing.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
  )
}

export const AdminBillingScreen = () => {
  const { t } = useLanguage()

  return (
    <AdminLayout title={t('admin.billing.title')} description={t('admin.billing.desc')}>
      <AdminBillingContent />
    </AdminLayout>
  )
}
